export interface ViewportCaptureRequest {
  fit: string[];
  /**
   * Targets that actually need visual approval. `fit` may additionally contain
   * protected references that must remain in frame for scale and alignment,
   * but those references must not consume the focused-review camera budget.
   */
  reviewTargetIds?: string[];
  context: "targets" | "environment";
  view: "adaptive" | "3d" | "plan" | "front";
  maxWidth: number;
  maxImages: number;
  occlusionMode: "auto" | "none" | "hide-walls";
  azimuthDeg?: number;
  elevationDeg?: number;
  distanceScale: number;
}

export interface InspectionCameraMetadata {
  id: string;
  label: string;
  kind: "perspective" | "front" | "side" | "plan" | "custom";
  fittedIds: string[];
  positionMm: [number, number, number];
  targetMm: [number, number, number];
  azimuthDeg: number;
  elevationDeg: number;
  score: number;
  visibilityRatio: number;
  frameCoverageRatio: number;
  clipped: boolean;
  sectioned: boolean;
  hiddenWallIds: string[];
  hiddenElementIds: string[];
  targetMetrics: Array<{
    elementId: string;
    visibilityRatio: number;
    frameCoverageRatio: number;
    clipped: boolean;
  }>;
}

export interface ViewportInspectionImage {
  image: { mediaType: "image/png"; dataBase64: string };
  width: number;
  height: number;
  view: string;
  fittedIds: string[];
  camera: InspectionCameraMetadata;
}

export interface ViewportCaptureResult {
  image: { mediaType: "image/png"; dataBase64: string };
  camera: InspectionCameraMetadata;
  additionalImages: ViewportInspectionImage[];
  /** Where the capture spent its time; audited so slow captures can be diagnosed from the record. */
  timingsMs?: {
    loadWaitMs: number;
    /** Espera pela prontidão de materiais/shaders antes de renderizar (orçamento de relógio). */
    readinessWaitMs?: number;
    candidateEvaluationMs: number;
    renderMs: number;
    /** Duração de cada imagem renderizada, na ordem do pacote. */
    imagesMs?: number[];
    /** Triângulos totais da cena no momento da captura (diagnóstico de peso). */
    sceneTriangles?: number;
    totalMs: number;
    candidatesEvaluated: number;
  };
  width: number;
  height: number;
  view: string;
  context: "targets" | "environment";
  fittedIds: string[];
  overview?: {
    environment: boolean;
    hasPerspective: boolean;
    hasPlan: boolean;
    hasFront: boolean;
    sectionedViewCount: number;
    imageCount: number;
  };
  inspection: Array<{
    elementId: string;
    name: string;
    /** Integridade do corpo renderizado: total × visíveis × desligadas pela avaliação dinâmica. */
    geometry?: {
      meshCount: number;
      visibleMeshCount: number;
      dynamicHiddenMeshCount: number;
      note?: string;
    };
    renderedBoundsMm: { minimum: [number, number, number]; maximum: [number, number, number] };
    closedRenderedBoundsMm: { minimum: [number, number, number]; maximum: [number, number, number] };
    poses: { installation: "closed"; visual: "current" };
    support: {
      kind: "wall" | "floor" | "ceiling" | "element" | "none";
      surfaceId: string | null;
      logicalGapMm: number | null;
      renderedGapMm: number | null;
      status: "supported" | "gap" | "penetrating" | "unverified";
    };
    contacts: Array<{
      kind: "wall" | "floor" | "ceiling" | "element" | "fixture";
      targetId: string | null;
      targetName: string;
      logicalGapMm: number | null;
      renderedGapMm: number;
      status: "touching" | "gap" | "penetrating" | "embedded";
      axisGapsMm: [number, number, number];
      metric: "closed_oriented_envelope";
    }>;
  }>;
  captureId: string;
  visualReview: {
    status: "ready" | "insufficient";
    candidatesEvaluated: number;
    selectedImages: number;
    coveredIds: string[];
    targets: Array<{
      elementId: string;
      bestVisibilityRatio: number;
      bestFrameCoverageRatio: number;
      qualifiedViewCount: number;
      status: "covered" | "insufficient";
    }>;
    issues: string[];
    accumulatedCaptureCount?: number;
  };
}

type Capturer = (request: ViewportCaptureRequest) => Promise<ViewportCaptureResult>;

let activeCapturer: Capturer | null = null;

/** The mounted Babylon viewport registers the only capture implementation. */
export function registerEditorViewportCapturer(capturer: Capturer): () => void {
  activeCapturer = capturer;
  return () => {
    if (activeCapturer === capturer) activeCapturer = null;
  };
}

export async function captureEditorViewport(request: ViewportCaptureRequest): Promise<ViewportCaptureResult> {
  const previousMode = useEditorStore.getState().mode;
  if (!activeCapturer && previousMode !== "3d") useEditorStore.getState().setMode("3d");
  try {
    const deadline = Date.now() + 8_000;
    while (!activeCapturer && Date.now() < deadline) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    if (!activeCapturer) throw new Error("A vista 3D não ficou pronta para a conferência visual.");
    return await activeCapturer(request);
  } finally {
    if (useEditorStore.getState().mode !== previousMode) useEditorStore.getState().setMode(previousMode);
  }
}
import { useEditorStore } from "./store";
